import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useState } from 'react';
import { CashButton } from './CashPrimitives';
export const cashPeriodValid = (value) => /^(?:[1-9]\d{3})-(?:0[1-9]|1[0-2])$/.test(value);
/** The parent owns the period, including an unfinished year. No implicit date fallback. */
export function CashPeriodPicker({ value, onChange, locale, label, disabled = false, min, max, required = false, compact = false, }) {
    const id = useId(), [open, setOpen] = useState(false);
    const words = {
        az: { year: 'İl', previous: 'Əvvəlki ay', next: 'Növbəti ay', month: 'Ay' },
        ru: { year: 'Год', previous: 'Предыдущий месяц', next: 'Следующий месяц', month: 'Месяц' },
        en: { year: 'Year', previous: 'Previous month', next: 'Next month', month: 'Month' },
    }[locale];
    const parts = value.split('-'), year = parts[0] || '', month = parts[1] || '';
    // Embedded browsers do not consistently ship Azerbaijani ICU month data.
    const names = {
        az: [
            'yanvar',
            'fevral',
            'mart',
            'aprel',
            'may',
            'iyun',
            'iyul',
            'avqust',
            'sentyabr',
            'oktyabr',
            'noyabr',
            'dekabr',
        ],
        ru: [
            'январь',
            'февраль',
            'март',
            'апрель',
            'май',
            'июнь',
            'июль',
            'август',
            'сентябрь',
            'октябрь',
            'ноябрь',
            'декабрь',
        ],
        en: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ],
    }[locale];
    const valid = cashPeriodValid(value), beforeMin = Boolean(valid && min && value < min);
    const move = (delta) => {
        if (!valid)
            return;
        const n = Number(year) * 12 + Number(month) - 1 + delta, y = Math.floor(n / 12);
        if (y < 1000 || y > 9999)
            return;
        const next = `${y}-${String((n % 12) + 1).padStart(2, '0')}`;
        if ((!min || next >= min) && (!max || next <= max))
            onChange(next);
    };
    return (_jsxs("fieldset", { className: "cash-period", disabled: disabled, "aria-labelledby": `${id}-label`, children: [_jsx("legend", { id: `${id}-label`, className: compact ? 'cash-visually-hidden' : undefined, children: label }), _jsxs("div", { className: "cash-period-controls", children: [_jsx(CashButton, { "aria-label": words.previous, disabled: !valid || Boolean(min && value <= min), onClick: () => move(-1), children: "\u2039" }), _jsx(CashButton, { "aria-label": `${label}: ${names[Number(month) - 1] || words.month}`, "aria-expanded": open, "aria-controls": `${id}-months`, onClick: () => setOpen(!open), children: names[Number(month) - 1] || words.month }), _jsx("input", { "aria-label": `${label}: ${words.year}`, onKeyDown: e => {
                            if (e.key === 'Enter')
                                e.preventDefault();
                        }, inputMode: "numeric", autoComplete: "off", value: year, maxLength: 4, required: required, pattern: "[1-9][0-9]{3}", "aria-invalid": Boolean(year && (!valid || beforeMin)), onChange: e => onChange(`${e.target.value.replace(/\D/g, '')}-${month}`) }), _jsx(CashButton, { "aria-label": words.next, disabled: !valid || (year === '9999' && month === '12') || Boolean(max && value >= max), onClick: () => move(1), children: "\u203A" })] }), open && (_jsx("div", { id: `${id}-months`, className: "cash-period-months", role: "group", "aria-label": label, children: names.map((name, i) => (_jsx(CashButton, { "aria-pressed": Number(month) === i + 1, disabled: Boolean(year.length === 4 && (min && `${year}-${String(i + 1).padStart(2, '0')}` < min || max && `${year}-${String(i + 1).padStart(2, '0')}` > max)), onClick: () => {
                        onChange(`${year}-${String(i + 1).padStart(2, '0')}`);
                        setOpen(false);
                    }, children: name }, i))) }))] }));
}
